<template>
  <div ref="containerRef" class="flex flex-col">
    <div class="flex text-background-fg/40">
      <div
        class="mb-1 block flex-1 text-xs font-medium text-background-fg/70"
        :class="{ 'text-error': hasError }"
      >
        {{ modifierGroup.label }}
      </div>
      <div
        class="ml-2 text-xs"
        :class="{ 'font-semibold text-error': hasError }"
      >
        {{ helpText }}
      </div>
    </div>

    <div class="flex flex-col">
      <div
        v-for="option in modifierGroup.options"
        :key="option.id"
        :class="[
          '-mb-px flex cursor-pointer border px-3 py-2 first:rounded-t-md last:rounded-b-md',
          modelValue.includes(option.id)
            ? 'border-primary bg-primary/10 ring-primary'
            : 'border-background-fg/8',
        ]"
        @click="() => onOptionClick(option)"
      >
        <div class="flex flex-1 items-center text-sm">
          <template v-if="!isSingleSelect">
            <IconCheckCircleFilled
              v-if="modelValue.includes(option.id)"
              class="mr-2 size-5 text-primary"
              aria-hidden="true"
            />
            <IconCircle
              v-else
              class="mr-2 size-5 text-background-fg/20"
              aria-hidden="true"
            />
          </template>
          <div class="mr-2 flex-1 leading-6">
            {{ option.label }}
          </div>
        </div>
        <div class="text-background-fg/40">
          {{
            formatToMerchantCurrencyString(
              option.priceOffset
            )
          }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  ProductModifierGroup,
  ProductModifierOption,
} from "@/services/product-modifiers/listProductModifiers"

const { formatToMerchantCurrencyString } = useCurrency()

const props = defineProps<{
  modifierGroup: ProductModifierGroup
  modelValue: string[]
  hasError: boolean
}>()

const emit = defineEmits<{
  (event: "update:modelValue", value: string[]): void
}>()

const containerRef = useTemplateRef("containerRef")

const scrollIntoView = () =>
  containerRef.value?.scrollIntoView({ behavior: "smooth" })

defineExpose({
  scrollIntoView,
})

const isSingleSelect = computed(
  () =>
    props.modifierGroup.maxOptions === 1 &&
    props.modifierGroup.minOptions === 1
)

const onOptionClick = (option: ProductModifierOption) => {
  if (
    isSingleSelect.value &&
    props.modelValue.includes(option.id)
  ) {
    return
  }

  if (isSingleSelect.value) {
    emit("update:modelValue", [option.id])
    return
  }

  if (props.modelValue.includes(option.id)) {
    emit(
      "update:modelValue",
      props.modelValue.filter((id) => id !== option.id)
    )
    return
  }

  if (props.modifierGroup.maxOptions === 1) {
    emit("update:modelValue", [option.id])
  }

  if (
    props.modelValue.length >=
    props.modifierGroup.maxOptions
  ) {
    return
  }

  emit("update:modelValue", [
    ...props.modelValue,
    option.id,
  ])
}

const helpText = computed(() => {
  if (
    props.modifierGroup.minOptions ===
    props.modifierGroup.maxOptions
  ) {
    return props.modifierGroup.minOptions === 1
      ? "Choose one"
      : `Choose ${props.modifierGroup.minOptions}`
  }

  return `Choose between ${props.modifierGroup.minOptions} and ${props.modifierGroup.maxOptions}`
})
</script>
